import { BulkUserRow } from '../../csvParser';

// Helpers for mapping bulk import results back to the in-memory user list.
// The import API validates all-or-nothing and reports failures by RowNumber,
// which is 1-based over the submitted CSV rows (active users in order).

/**
 * Original indices of the rows that will be (or were) submitted, in submission order.
 * Element at position i is the original index of submitted row number i + 1.
 */
export const getSubmittedIndexes = (totalRows: number, removedRows: Set<number>): number[] => {
    const indexes: number[] = [];
    for (let i = 0; i < totalRows; i++) {
        if (!removedRows.has(i)) {
            indexes.push(i);
        }
    }
    return indexes;
};

/**
 * Retry is allowed only when every failed row has been removed from the import
 * (the API rejects the whole batch if any invalid row remains) and there is
 * still at least one user left to import.
 */
export const canRetryImport = (
    failedRowNumbers: number[],
    submittedIndexes: number[],
    removedRows: Set<number>,
    activeUserCount: number
): boolean => {
    if (activeUserCount === 0) return false;
    return failedRowNumbers.every(rowNumber => removedRows.has(submittedIndexes[rowNumber - 1]));
};

export type BulkUsersMode = 'import' | 'invite';

const escapeCsv = (val: string) =>
    val.includes(',') || val.includes('"') || val.includes('\n') ? `"${val.replace(/"/g, '""')}"` : val;

// The server re-parses this by header alias, so the header row is the contract, not the column order.
export const buildCsvContent = (users: BulkUserRow[], mode: BulkUsersMode): string => {
    const header = mode === 'invite' ? 'Email' : 'UserName,Email,FirstName,LastName,PhoneNumber';
    const rows = users.map(u =>
        (mode === 'invite' ? [u.email] : [u.userName, u.email, u.firstName, u.lastName, u.phoneNumber])
            .map(escapeCsv)
            .join(',')
    );
    return [header, ...rows].join('\n');
};
